import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Container,
  Grid2,
} from "@mui/material";
import {
  determineHeaderTimes,
  generateTimeSlotOptions,
  notifErr,
} from "../../components/Functions";
import handleAxiosError from "../../components/ErrorHandler";
import { Loading } from "../../components/Loading";
import { Course } from "../../model/entity/Course";
import { TrainingArea } from "../../model/entity/TrainingArea";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { TimeSlotDTO } from "../../model/entity/TimeSlotDTO";
import { getFetcher } from "../../components/Fetchers";

const daysDict = {
  MONDAY: "Pondělí",
  TUESDAY: "Úterý",
  WEDNESDAY: "Středa",
  THURSDAY: "Čtvrtek",
  FRIDAY: "Pátek",
  SATURDAY: "Sobota",
  SUNDAY: "Neděle",
};

const ScheduleRoster = () => {
  const navigate = useNavigate();
  const [headerTimes, setHeaderTimes] = useState<string[] | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [areas, setAreas] = useState<TrainingArea[] | null>(null);
  const [selectedDay, setSelectedDay] = useState(
    dayjs().format("dddd").toUpperCase()
  );

  const [hoveredTimeSlotId, setHoveredTimeSlotId] = useState<number | null>(
    null
  );

  const handleTimeSlotId = (slotId: number | null) => {
    if (slotId !== hoveredTimeSlotId) setHoveredTimeSlotId(slotId);
  };

  useEffect(() => {
    const fetchData = async () => {
      const config = { day: selectedDay };
      try {
        const courseRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/courses/all-active-by-day`,
          config
        );
        setCourses(courseRes.data);
        const timeSlots: TimeSlotDTO[] = courseRes.data
          .map((c: Course) => c.timeSlots)
          .flat()
          .filter((t: TimeSlotDTO) => t.day === selectedDay);

        const { from, to } = determineHeaderTimes(timeSlots);
        setHeaderTimes(generateTimeSlotOptions(from, to, to !== 24));
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  }, [selectedDay]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const areasRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/training-areas/sorted-by-code`
        );
        setAreas(areasRes.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  }, []);

  const findCourseForCell = (area: TrainingArea, time: string) => {
    let slotId = null;
    const matchingCourse = courses.find((course) =>
      course.timeSlots.some((slot) => {
        const slotStart = slot.startTime.substring(0, 5); // the times came in format "HH:MM"
        const slotEnd = slot.endTime.substring(0, 5);

        const isOnCurrentDay = slot.day === selectedDay;
        const isWithinTime =
          time >= slotStart && (time < slotEnd || slotEnd === "00:00");
        const isAtThisArea = slot.timeSlotAtTrainingAreas.some(
          (t) => t.trainingArea.id === area.id
        );
        if (isOnCurrentDay && isWithinTime && isAtThisArea) {
          slotId = slot.id;
          return true;
        } else return false;
      })
    );

    return { matchingCourse, slotId };
  };

  if (!courses || !areas || !headerTimes) return <Loading />;

  const getCellSpan = (course: Course, currentTime: string) => {
    const slot = course.timeSlots.find(
      (slot) =>
        slot.day === selectedDay &&
        currentTime >= slot.startTime.substring(0, 5) &&
        (currentTime < slot.endTime.substring(0, 5) ||
          slot.endTime.substring(0, 5) === "00:00")
    );

    if (!slot) return 1;
    const endTime = slot.endTime.substring(0, 5);

    const startTimeIndex = headerTimes.indexOf(slot.startTime.substring(0, 5));
    const endTimeIndex =
      endTime === "00:00" ? headerTimes.length : headerTimes.indexOf(endTime);
    return endTimeIndex - startTimeIndex;
  };

  return (
    <Container className="scheduleContainer" maxWidth={false}>
      <TableContainer
        className="scheduleTableContainer"
        onMouseLeave={() => handleTimeSlotId(null)}
      >
        <Box className="daySelectHeader">
          {Object.entries(daysDict).map((d) => (
            <Box
              key={d[0]}
              className={`daySelectHeaderItem ${
                d[0] === selectedDay ? "selectedDaySelectHeaderItem" : ""
              }`}
              onClick={() => {
                setSelectedDay(d[0]);
              }}
            >
              <Typography variant="body1" fontWeight="inherit">
                {d[1]}
              </Typography>
            </Box>
          ))}
        </Box>
        <Table>
          <TableHead>
            <TableRow
              key="timeHeader"
              onMouseEnter={() => handleTimeSlotId(null)}
            >
              <TableCell
                key={"cancel-mode-cell"}
                className="scheduleHeaderTimeCell"
              />
              {headerTimes.map((time) => (
                <TableCell key={`${time}`} className="scheduleHeaderTimeCell">
                  {time}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area, areaIndex) => (
              <TableRow key={areaIndex}>
                <TableCell
                  key={area.id}
                  className="scheduleTrainingAreaCell"
                  onClick={() =>
                    navigate(`/schedule/training-areas/edit/${area.id}`)
                  }
                  onMouseEnter={() => handleTimeSlotId(null)}
                >
                  {area.code}
                </TableCell>
                {headerTimes.map((time, timeIndex) => {
                  const res1 = findCourseForCell(area, time);
                  const cellKey = `${area.id}-${time}`;
                  if (timeIndex > 0) {
                    const prevTime = headerTimes[timeIndex - 1];
                    const res2 = findCourseForCell(area, prevTime);
                    if (
                      res1.matchingCourse &&
                      res2.matchingCourse &&
                      res2.matchingCourse.id === res1.matchingCourse?.id &&
                      res1.slotId === res2.slotId
                    ) {
                      return null;
                    }
                  }

                  if (res1.matchingCourse) {
                    const course = res1.matchingCourse;
                    const span = getCellSpan(course, time);

                    let above = false;
                    let below = false;
                    const objectAbove =
                      areaIndex !== 0
                        ? findCourseForCell(areas[areaIndex - 1], time)
                        : null;
                    const objectBelow =
                      areaIndex + 1 !== areas.length
                        ? findCourseForCell(areas[areaIndex + 1], time)
                        : null;
                    if (objectAbove) above = objectAbove.slotId === res1.slotId;
                    if (objectBelow) below = objectBelow.slotId === res1.slotId;

                    const isTop = !above && below;
                    const isBottom = above && !below;
                    const isOnlyOne = !above && !below;

                    return (
                      <TableCell
                        key={cellKey}
                        className="scheduleCourseCell"
                        colSpan={span}
                        sx={{
                          background: course.color,
                          borderTop:
                            isTop || isOnlyOne ? "1px solid white" : "none",
                          borderBottom:
                            isBottom || isOnlyOne ? "1px solid white" : "none",
                          borderLeft: "1px solid white",
                          borderRight: "1px solid white",
                          opacity: hoveredTimeSlotId === res1.slotId ? 0.7 : 1,
                        }}
                        onClick={() =>
                          navigate(`/overview/courses/edit/${course.id}`)
                        }
                        onMouseMove={() => handleTimeSlotId(res1.slotId!)}
                      >
                        <Box className="scheduleCourseContainer">
                          {!above && (
                            <Typography
                              className="courseContainerText courseContainerTextHoverable"
                              variant="caption"
                              sx={{ cursor: "pointer" }}
                            >
                              <b>{course.name}</b> (
                              {course.memberInCourses.length}/{course.capacity})
                            </Typography>
                          )}
                          {!below && (
                            <Grid2 sx={{ marginTop: "auto" }}>
                              {course.coachLeadsCourses.length > 0 ? (
                                course.coachLeadsCourses.map((c, index) => (
                                  <Typography
                                    key={c.coach.id}
                                    className="courseContainerText courseContainerTextHoverable"
                                    variant="caption"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/overview/coaches/edit/${c.coach.id}`
                                      );
                                    }}
                                  >
                                    {c.coach.nickname}
                                    {index !==
                                    course.coachLeadsCourses.length - 1
                                      ? ", "
                                      : ""}
                                  </Typography>
                                ))
                              ) : (
                                <Typography className="courseContainerText" variant="caption">
                                  bez trenéra
                                </Typography>
                              )}
                            </Grid2>
                          )}
                        </Box>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={cellKey}
                      className="scheduleEmptyCell"
                      onMouseEnter={() => handleTimeSlotId(null)}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ScheduleRoster;
